import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Duplicate email / phone checks for the patient contact fields.
 *
 * Flutter `patient_data_source_impl.dart`:
 *   GET /api/Patients/ValidateEmail?emailAddress=&patientId=        → bool
 *   GET /api/Patients/ValidatePhoneNumber?contactNumber=&patientId= → bool
 * `true` means the value is ALREADY used by another patient. Query keys are
 * camelCase (`emailAddress` / `contactNumber` / `patientId`) per
 * `FetchMatchingPatientParams.toJson`; ASP.NET query binding is
 * case-insensitive so either casing binds, but we mirror Flutter.
 *
 * `+` in a phone number must reach the backend as `%2B` — `URLSearchParams`
 * encodes it that way automatically (matches Flutter's manual replace).
 *
 * The caller passes `null` to disable the check (e.g. create mode, minor
 * patient, value unchanged from the loaded record, or invalid format). The
 * backend returns a bare JSON boolean; we coerce defensively in case it
 * arrives as text/plain.
 */
function toBool(raw: unknown): boolean {
  return raw === true || raw === 'true';
}

export interface DuplicateEmailParams {
  emailAddress: string;
  patientId: string | null;
}

export function useCheckDuplicateEmail(params: DuplicateEmailParams | null) {
  return useQuery({
    queryKey: ['validate-email-dup', params?.emailAddress, params?.patientId],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set('emailAddress', params!.emailAddress);
      if (params!.patientId) qs.set('patientId', params!.patientId);
      const raw = await get<unknown>(`/api/Patients/ValidateEmail?${qs.toString()}`, {
        signal,
      });
      return toBool(raw);
    },
    enabled: !!params && params.emailAddress.length > 0,
    // A duplicate-check failure is advisory — never surface the global toast.
    meta: { skipGlobalError: true },
    staleTime: 60_000,
  });
}

export interface DuplicatePhoneParams {
  contactNumber: string;
  patientId: string | null;
}

export function useCheckDuplicatePhone(params: DuplicatePhoneParams | null) {
  return useQuery({
    queryKey: ['validate-phone-dup', params?.contactNumber, params?.patientId],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set('contactNumber', params!.contactNumber);
      if (params!.patientId) qs.set('patientId', params!.patientId);
      const raw = await get<unknown>(
        `/api/Patients/ValidatePhoneNumber?${qs.toString()}`,
        { signal },
      );
      return toBool(raw);
    },
    enabled: !!params && params.contactNumber.length > 0,
    meta: { skipGlobalError: true },
    staleTime: 60_000,
  });
}
